import { useState, type ReactNode } from 'react';
import { Button, EmptyState, InboxArt, PlainLink } from '@paige/ui';
import { useFormat, type Formatter } from '@paige/format';
import type { CompanyPickerProps } from '../CompanyPicker';
import type { FlowLinks } from '../flow/FlowDetail';
import { ProblemActionDialog, type PendingAction } from './ProblemActionDialog';
import { ProblemRow } from './ProblemRow';
import { ProblemsToolbar } from './ProblemsToolbar';
import type {
  ProblemActRequest,
  ProblemActResult,
  ProblemFilesPage,
  ProblemGroup,
  Problems,
  ProblemsQuery,
} from './problem-types';
import '../admin.css';
import './problems.css';

export interface ProblemsBoardProps {
  problems: Problems;
  /** What was asked for: the search, the order and how many rows. */
  query: ProblemsQuery;
  /** Asks again with another search or order; the ledger narrows and sorts, never this screen. */
  onQuery: (query: ProblemsQuery) => void;
  /** The company picker: one company's rows, or every company's. */
  company?: CompanyPickerProps | undefined;
  /** Reads more rows; absent when every matching row is shown. */
  onMore?: (() => void) | undefined;
  links: FlowLinks;
  /** A page of one row's files, oldest first. */
  loadFiles: (group: ProblemGroup, offset: number) => Promise<ProblemFilesPage>;
  /**
   * Does it and resolves with what changed; rejects with the sentence to show. Absent for a staff role the API
   * refuses (it allows support and engineering): then the rows carry no action, and say who can.
   */
  onAct?: ((request: ProblemActRequest) => Promise<ProblemActResult>) | undefined;
  /** Where "Stages and queues" in the summary leads. */
  queuesHref: string;
}

interface Tile {
  key: 'ours' | 'theirs' | 'known';
  label: string;
  note: string;
}

/** Three numbers a glance takes in: what is ours, what is the customer's, what is already known. In files. */
const TILES: Tile[] = [
  { key: 'ours', label: 'Ours to fix', note: 'to retry, or hand to engineering' },
  { key: 'theirs', label: "Customer's move", note: 'the customer fixes; you can close them' },
  { key: 'known', label: 'Acknowledged', note: 'known, and not alerting' },
];

type SummaryProps = Pick<ProblemsBoardProps, 'problems' | 'queuesHref' | 'links'> & {
  f: Formatter;
  /** The search in force: then the count is of what matches, and is not Stages and queues' count. */
  searched: string;
};

/** The count in a sentence, and how it squares with Stages and queues, test companies hidden or not. */
function Agree({ problems, queuesHref, links, f, searched }: SummaryProps): ReactNode {
  const Link = links.Link ?? PlainLink;
  const { total, hidden } = problems;
  const queues = <Link href={queuesHref}>Stages and queues</Link>;
  return (
    <p className="pb-agree" data-total-items={total.items}>
      {f.int(total.items)} parked in {f.int(total.files)} {total.files === 1 ? 'file' : 'files'}{' '}
      across {f.int(total.companies)} {total.companies === 1 ? 'company' : 'companies'}.{' '}
      {searched ? (
        <>Matching &quot;{searched}&quot;.</>
      ) : hidden.items > 0 ? (
        <>
          {queues} shows {f.int(total.items + hidden.items)} parked: these {f.int(total.items)}, and{' '}
          {f.int(hidden.items)} in test companies that are hidden here.
        </>
      ) : (
        <>
          The same {f.int(total.items)} that {queues} shows as parked.
        </>
      )}
    </p>
  );
}

function Summary(props: SummaryProps): ReactNode {
  const { problems, f } = props;
  return (
    <section className="pb-summary" aria-label="Summary">
      <div className="pb-tiles">
        {TILES.map((t) => {
          const n = problems.tiles[t.key];
          return (
            <div
              key={t.key}
              className={`pb-tile pb-tile--${t.key}`}
              data-tile={t.key}
              data-empty={n === 0}
            >
              <strong className="pb-tile__n">{f.int(n)}</strong>
              <span className="pb-tile__label">{t.label}</span>
              <span className="pb-tile__note">
                {n === 1 ? 'file' : 'files'} {t.note}
              </span>
            </div>
          );
        })}
      </div>
      <Agree {...props} />
    </section>
  );
}

/** Why there is no row: nothing matches the search, only test companies have something, or nothing is parked. */
function Nothing({ problems, searched }: { problems: Problems; searched: string }): ReactNode {
  const f = useFormat();
  const hidden = problems.hidden.items;
  if (searched)
    return (
      <EmptyState
        compact
        title="Nothing matches"
        description={`No company or job called "${searched}" has anything parked. Clear the search to see every row.`}
      />
    );
  const tests = `Nothing is parked for a customer. ${f.int(hidden)} parked in test companies are hidden: switch off Hide test companies to see them.`;
  const none =
    'No file, page or document is parked right now. When something stops, it appears here with what to do about it.';
  return (
    <EmptyState
      illustration={<InboxArt />}
      title="Nothing is waiting for anyone"
      description={hidden > 0 ? tests : none}
    />
  );
}

/** "Showing 25 of 140 rows" and the way to more of them. */
function More({ problems, onMore }: Pick<ProblemsBoardProps, 'problems' | 'onMore'>): ReactNode {
  const f = useFormat();
  if (problems.matching <= problems.groups.length) return null;
  return (
    <p className="pb-more">
      Showing {f.int(problems.groups.length)} of {f.int(problems.matching)} rows.{' '}
      {onMore ? (
        <Button variant="secondary" size="sm" onClick={onMore}>
          Show more
        </Button>
      ) : (
        'Search by company or job name to narrow them.'
      )}
    </p>
  );
}

function Rows(
  props: ProblemsBoardProps & { onAsk: ((p: PendingAction) => void) | undefined },
): ReactNode {
  const { problems, links, loadFiles, onAsk } = props;
  const [open, setOpen] = useState<string | null>(null);
  return (
    <ul className="pb-rows" aria-label="Problems">
      {problems.groups.map((g) => (
        <ProblemRow
          key={g.id}
          group={g}
          links={links}
          open={open === g.id}
          onToggle={() => setOpen(open === g.id ? null : g.id)}
          loadFiles={loadFiles}
          stamp={String(problems.version)}
          onAsk={onAsk}
        />
      ))}
    </ul>
  );
}

/** What the rows are narrowed to, in words: the search typed, or the company picked; empty for every row. */
function narrowedTo({ query, company }: ProblemsBoardProps): string {
  return query.text || company?.value?.name || query.company || '';
}

/**
 * Level 0, screen 3: work that stopped and waits for a person, one row per company and reason, counted in files,
 * pages and documents. Every next step is a control: retry, resolve with a note, acknowledge until a date, put a
 * capture back; a row opens to its files, a file to what happened to it. The rows are a page of what the ledger
 * narrowed and sorted; the counts above them are over everything that matches.
 */
export function ProblemsBoard(props: ProblemsBoardProps): ReactNode {
  const { problems, query, onAct } = props;
  const f = useFormat();
  const [pending, setPending] = useState<PendingAction | null>(null);
  const empty = problems.groups.length === 0;
  const narrowed = narrowedTo(props);
  if (empty && !narrowed) return <Nothing problems={problems} searched="" />;
  return (
    <div className="pb" data-problems={problems.total.items}>
      <Summary
        problems={problems}
        queuesHref={props.queuesHref}
        links={props.links}
        f={f}
        searched={query.text}
      />
      <ProblemsToolbar query={query} onQuery={props.onQuery} company={props.company} />
      {empty ? <Nothing problems={problems} searched={narrowed} /> : null}
      <Rows {...props} onAsk={onAct ? setPending : undefined} />
      <More problems={problems} onMore={props.onMore} />
      {onAct ? (
        <ProblemActionDialog pending={pending} onClose={() => setPending(null)} onConfirm={onAct} />
      ) : null}
    </div>
  );
}
